import React from 'react';
import { Box, TextField, type SxProps, type TextFieldProps, type Theme } from '@mui/material';

import { NotepadBox } from './NotepadBox';

type TextFieldInputSlotProps = NonNullable<NonNullable<TextFieldProps['slotProps']>['input']>;
type SlotPropsResolver<TSlotProps> = TSlotProps extends (
  ownerState: infer TOwnerState,
) => infer TResolvedSlotProps
  ? (ownerState: TOwnerState) => TResolvedSlotProps
  : never;
type TextFieldInputSlotPropsResolver = SlotPropsResolver<TextFieldInputSlotProps>;
type TextFieldInputOwnerState = Parameters<TextFieldInputSlotPropsResolver>[0];
type ResolvedTextFieldInputSlotProps = ReturnType<TextFieldInputSlotPropsResolver>;

export interface NotepadTextFieldProps extends Omit<TextFieldProps, 'sx'> {
  InputProps?: TextFieldInputSlotProps;
  /** Distance from left edge to red margin line (default: 35px) */
  marginLinePosition?: string;
  /** Padding left to add space between margin line and content (default: 4.5 = 36px) */
  contentPaddingLeft?: number;
  /** Padding top and bottom for the NotepadBox inner content (default: 0 when used in flex container) */
  contentPaddingY?: number;
  /** Additional sx styles for the TextField */
  textFieldSx?: TextFieldProps['sx'];
  /** Additional sx styles for the NotepadBox container */
  containerSx?: React.ComponentProps<typeof NotepadBox>['sx'];
}

/**
 * NotepadTextField - A TextField component with notepad styling
 * Wraps MUI TextField with NotepadBox styling for consistent notepad appearance
 */
export const NotepadTextField: React.FC<NotepadTextFieldProps> = ({
  marginLinePosition = '35px',
  contentPaddingLeft = 4.5,
  contentPaddingY,
  textFieldSx,
  containerSx,
  InputProps,
  ...textFieldProps
}) => {
  const notepadInputSx = {
    bgcolor: 'transparent',
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  } satisfies SxProps<Theme>;

  const inputSlotProps: TextFieldInputSlotProps = (ownerState: TextFieldInputOwnerState) => {
    const resolvedInputProps: ResolvedTextFieldInputSlotProps | undefined =
      typeof InputProps === 'function' ? InputProps(ownerState) : InputProps;

    return {
      ...resolvedInputProps,
      sx: [
        notepadInputSx,
        ...(Array.isArray(resolvedInputProps?.sx)
          ? resolvedInputProps.sx
          : resolvedInputProps?.sx
            ? [resolvedInputProps.sx]
            : []),
      ] as SxProps<Theme>,
    };
  };

  return (
    <NotepadBox
      marginLinePosition={marginLinePosition}
      contentPaddingLeft={0}
      contentPaddingY={contentPaddingY ?? 0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        ...containerSx,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          pl: contentPaddingLeft,
          pr: 3,
          py: 0,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <TextField
          {...textFieldProps}
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            '& .MuiInputBase-root': {
              bgcolor: 'transparent',
            },
            ...textFieldSx,
          }}
          slotProps={{
            input: inputSlotProps,
          }}
        />
      </Box>
    </NotepadBox>
  );
};
